import { useEffect, useRef } from "react";

/**
 * Hook that uses IntersectionObserver to trigger a callback
 * when a sentinel element scrolls into view.
 *
 * @param onLoadMore - Callback to invoke when more data should be loaded
 * @param options.enabled - Whether the observer should be active (set to false when loading or no more pages)
 * @param options.rootMargin - IntersectionObserver rootMargin (default: "200px" to trigger slightly before visible)
 * @returns A ref to attach to a sentinel <div> at the bottom of the scrollable content
 */
const useInfiniteScroll = (
  onLoadMore: () => void,
  options: { enabled: boolean; rootMargin?: string },
) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onLoadMore);

  // Keep callback ref in sync without re-creating the observer
  useEffect(() => {
    callbackRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    if (!options.enabled) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          callbackRef.current();
        }
      },
      { rootMargin: options.rootMargin ?? "200px" },
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [options.enabled, options.rootMargin]);

  return sentinelRef;
};

export default useInfiniteScroll;
